import { detectImageCandidates } from '@/lib/templatePipeline';
import { classifyCandidates } from '@/lib/templateClassify';
import type { LayoutTemplateAlbumCategory } from '@/lib/api';
import { putPendingUpload, type QueuedTemplate } from '@/lib/idbTemplateUpload';

export interface PrepareUploadOptions {
  /** When set, the resulting review-pane save will PATCH this template id
   *  instead of creating a new row. Applied to ALL files in the batch — in
   *  practice the Replace-SVG flow only ever passes one file, so this is fine. */
  editingTemplateId?: string;
  /** Initial metadata to seed in the review pane. Used by TemplateEditDialog
   *  so the form pre-fills with the template's current name/category/etc. */
  initialMetadata?: {
    name?: string;
    slug?: string;
    category?: LayoutTemplateAlbumCategory;
    isVisible?: boolean;
    isAiEligible?: boolean;
  };
}

export interface PrepareUploadsResult {
  /** Templates that detected + classified successfully, in the same order as
   *  the input files. Empty when nothing succeeded. */
  ok: QueuedTemplate[];
  /** Per-file errors for files that failed validation, detection, or
   *  classification. The caller surfaces these in an admin banner. */
  failed: Array<{ fileName: string; error: string }>;
}

/**
 * Process a batch of SVG files through detect + classify IN PARALLEL.
 *
 * Each file is independently wrapped in try/catch so one bad detection (e.g.
 * SVG with zero <image> elements) doesn't abort the whole batch — failures
 * land in `failed`, successes in `ok`. When at least one file succeeded we
 * also write the resulting queue to IDB via putPendingUpload, so the caller
 * can navigate straight to /admin/layouts/review without an extra round trip.
 *
 * IndexedDB (not sessionStorage) because real Canva SVGs embed base64 PNGs
 * and routinely exceed the ~5 MB sessionStorage quota.
 */
export async function prepareTemplateUploads(
  files: File[],
  options: PrepareUploadOptions = {},
): Promise<PrepareUploadsResult> {
  const results = await Promise.all(
    files.map(async (file): Promise<{ ok: QueuedTemplate } | { failed: { fileName: string; error: string } }> => {
      try {
        const svgString = await file.text();
        const { candidates, pageBackgrounds, pageBackgroundColor } =
          await detectImageCandidates(svgString);
        if (candidates.length === 0) {
          throw new Error('Aucun élément <image> détecté dans ce SVG.');
        }
        const predictions = await classifyCandidates(candidates);
        return {
          ok: {
            svgString,
            fileName: file.name,
            candidates,
            predictions,
            pageBackgrounds,
            pageBackgroundColor,
            editingTemplateId: options.editingTemplateId,
            initialMetadata: options.initialMetadata,
          },
        };
      } catch (err) {
        return {
          failed: {
            fileName: file.name,
            error: err instanceof Error ? err.message : String(err),
          },
        };
      }
    }),
  );

  const ok: QueuedTemplate[] = [];
  const failed: Array<{ fileName: string; error: string }> = [];
  for (const r of results) {
    if ('ok' in r) ok.push(r.ok);
    else failed.push(r.failed);
  }

  if (ok.length > 0) {
    await putPendingUpload({ queue: ok });
  }

  return { ok, failed };
}
